import { type MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'

import { Button } from '#app/components/ui/button.tsx'
import { Cover } from '#app/components/ui/cover.tsx'
import { Features } from './features.tsx'
import { Footer } from './footer.tsx'
import { Pricing } from './pricing.tsx'

export const meta: MetaFunction = () => [{ title: 'Subdark' }]

export default function Root() {
	return (
		<div>
			<Hero />
			<Pricing />
			<Footer />
		</div>
	)
}

function Hero() {
	return (
		<main className="relative flex min-h-screen flex-col justify-center overflow-hidden px-4 py-40 md:px-8 md:pt-64">
			<Background />
			<div className="space-y-8 text-center">
				<h1 className="relative z-20 mx-auto max-w-xl animate-slide-top text-4xl font-semibold [animation-delay:0.2s] [animation-fill-mode:backwards] sm:text-6xl">
					Launch your API in <br />
					<Cover className="mt-2">30-minutes</Cover>
				</h1>
				<h5 className="relative z-20 mx-auto max-w-2xl animate-slide-top text-lg text-neutral-600 [animation-delay:0.4s] [animation-fill-mode:backwards] dark:text-neutral-400">
					Start, grow, and scale your APIs into revenue by providing a
					world-class, white-labeled developer experience for your customers.
				</h5>
				<YCombinator />
				<div className="animate-slide-top pt-4 [animation-delay:0.8s] [animation-fill-mode:backwards]">
					<Link to="https://cal.com/stanliu/subdark-demo" target="_blank">
						<Button size="lg">Book a demo</Button>
					</Link>
				</div>
			</div>
			<Features />
		</main>
	)
}

function YCombinator() {
	return (
		<div className="flex animate-slide-top flex-col gap-y-1.5 text-center [animation-delay:0.6s] [animation-fill-mode:backwards]">
			<div className="text-xs uppercase tracking-wide text-muted-foreground">
				Backed by
			</div>
			<span className="mx-auto mt-1 block dark:hidden">
				<img src="/img/yc.svg" alt="YC logo" className="h-5" />
			</span>
			<span className="mx-auto hidden items-center gap-1.5 dark:flex">
				<img src="/img/yc-dark.svg" alt="YC logo" className="h-5 w-5" />
				<span className="caption text-center">Combinator</span>
			</span>
		</div>
	)
}

function Background() {
	const lines = {
		'--background': '#ffffff',
		'--color': 'rgba(0, 0, 0, 0.2)',
		'--height': '5px',
		'--width': '1px',
		'--fade-stop': '90%',
		'--offset': '150px',
		'--color-dark': 'rgba(255, 255, 255, 0.3)',
		maskComposite: 'exclude',
	}
	return (
		<div className="pointer-events-none absolute inset-0 -z-10 grid h-full w-full -rotate-45 transform select-none grid-cols-2 gap-10 md:grid-cols-4">
			<div className="relative h-full w-full">
				<div
					style={lines}
					className="absolute left-0 top-[calc(var(--offset)/2*-1)] z-30 h-[calc(100%+var(--offset))] w-[var(--width)] bg-[linear-gradient(to_bottom,var(--color),var(--color)_50%,transparent_0,transparent)] [background-size:var(--width)_var(--height)] [mask-composite:exclude] [mask:linear-gradient(to_top,var(--background)_var(--fade-stop),transparent),_linear-gradient(to_bottom,var(--background)_var(--fade-stop),transparent),_linear-gradient(black,black)] dark:bg-[linear-gradient(to_bottom,var(--color-dark),var(--color-dark)_50%,transparent_0,transparent)]"
				></div>
				<div
					style={lines}
					className="absolute left-auto right-0 top-[calc(var(--offset)/2*-1)] z-30 h-[calc(100%+var(--offset))] w-[var(--width)] bg-[linear-gradient(to_bottom,var(--color),var(--color)_50%,transparent_0,transparent)] [background-size:var(--width)_var(--height)] [mask-composite:exclude] [mask:linear-gradient(to_top,var(--background)_var(--fade-stop),transparent),_linear-gradient(to_bottom,var(--background)_var(--fade-stop),transparent),_linear-gradient(black,black)] dark:bg-[linear-gradient(to_bottom,var(--color-dark),var(--color-dark)_50%,transparent_0,transparent)]"
				></div>
			</div>
			<div className="relative h-full w-full">
				<div
					style={lines}
					className="absolute left-0 top-[calc(var(--offset)/2*-1)] z-30 h-[calc(100%+var(--offset))] w-[var(--width)] bg-[linear-gradient(to_bottom,var(--color),var(--color)_50%,transparent_0,transparent)] [background-size:var(--width)_var(--height)] [mask-composite:exclude] [mask:linear-gradient(to_top,var(--background)_var(--fade-stop),transparent),_linear-gradient(to_bottom,var(--background)_var(--fade-stop),transparent),_linear-gradient(black,black)] dark:bg-[linear-gradient(to_bottom,var(--color-dark),var(--color-dark)_50%,transparent_0,transparent)]"
				></div>
				<div
					style={lines}
					className="absolute left-auto right-0 top-[calc(var(--offset)/2*-1)] z-30 h-[calc(100%+var(--offset))] w-[var(--width)] bg-[linear-gradient(to_bottom,var(--color),var(--color)_50%,transparent_0,transparent)] [background-size:var(--width)_var(--height)] [mask-composite:exclude] [mask:linear-gradient(to_top,var(--background)_var(--fade-stop),transparent),_linear-gradient(to_bottom,var(--background)_var(--fade-stop),transparent),_linear-gradient(black,black)] dark:bg-[linear-gradient(to_bottom,var(--color-dark),var(--color-dark)_50%,transparent_0,transparent)]"
				></div>
			</div>
			<div className="relative h-full w-full bg-gradient-to-b from-transparent via-neutral-100 to-transparent dark:via-neutral-800">
				<div
					style={lines}
					className="absolute left-0 top-[calc(var(--offset)/2*-1)] z-30 h-[calc(100%+var(--offset))] w-[var(--width)] bg-[linear-gradient(to_bottom,var(--color),var(--color)_50%,transparent_0,transparent)] [background-size:var(--width)_var(--height)] [mask-composite:exclude] [mask:linear-gradient(to_top,var(--background)_var(--fade-stop),transparent),_linear-gradient(to_bottom,var(--background)_var(--fade-stop),transparent),_linear-gradient(black,black)] dark:bg-[linear-gradient(to_bottom,var(--color-dark),var(--color-dark)_50%,transparent_0,transparent)]"
				></div>
				<div
					style={lines}
					className="absolute left-auto right-0 top-[calc(var(--offset)/2*-1)] z-30 h-[calc(100%+var(--offset))] w-[var(--width)] bg-[linear-gradient(to_bottom,var(--color),var(--color)_50%,transparent_0,transparent)] [background-size:var(--width)_var(--height)] [mask-composite:exclude] [mask:linear-gradient(to_top,var(--background)_var(--fade-stop),transparent),_linear-gradient(to_bottom,var(--background)_var(--fade-stop),transparent),_linear-gradient(black,black)] dark:bg-[linear-gradient(to_bottom,var(--color-dark),var(--color-dark)_50%,transparent_0,transparent)]"
				></div>
			</div>
			<div className="relative h-full w-full">
				<div
					style={lines}
					className="absolute left-0 top-[calc(var(--offset)/2*-1)] z-30 h-[calc(100%+var(--offset))] w-[var(--width)] bg-[linear-gradient(to_bottom,var(--color),var(--color)_50%,transparent_0,transparent)] [background-size:var(--width)_var(--height)] [mask-composite:exclude] [mask:linear-gradient(to_top,var(--background)_var(--fade-stop),transparent),_linear-gradient(to_bottom,var(--background)_var(--fade-stop),transparent),_linear-gradient(black,black)] dark:bg-[linear-gradient(to_bottom,var(--color-dark),var(--color-dark)_50%,transparent_0,transparent)]"
				></div>
				<div
					style={lines}
					className="absolute left-auto right-0 top-[calc(var(--offset)/2*-1)] z-30 h-[calc(100%+var(--offset))] w-[var(--width)] bg-[linear-gradient(to_bottom,var(--color),var(--color)_50%,transparent_0,transparent)] [background-size:var(--width)_var(--height)] [mask-composite:exclude] [mask:linear-gradient(to_top,var(--background)_var(--fade-stop),transparent),_linear-gradient(to_bottom,var(--background)_var(--fade-stop),transparent),_linear-gradient(black,black)] dark:bg-[linear-gradient(to_bottom,var(--color-dark),var(--color-dark)_50%,transparent_0,transparent)]"
				></div>
			</div>
		</div>
	)
}
